import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import { useQueryClient } from "@tanstack/react-query";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import { useTRPC } from "./trpc";

/**
 * The tabs of the Chat page (`/chat`), in the strip's order: the room as it stands, the report
 * queue, and the mutes in force. The tab rides the URL (`?tab=`), so a tab is a link.
 */
export const CHAT_TABS = ["room", "reports", "mutes"] as const;
export type ChatTab = (typeof CHAT_TABS)[number];

export const CHAT_TAB_LABELS: Record<ChatTab, string> = {
  room: "Room",
  reports: "Reports",
  mutes: "Mutes",
};

export function isChatTab(value: unknown): value is ChatTab {
  return typeof value === "string" && (CHAT_TABS as readonly string[]).includes(value);
}

export interface ChatSearch {
  tab?: ChatTab;
}

/** The route's `validateSearch`: an unknown tab is dropped, and the page shows the room. */
export function parseChatSearch(search: Record<string, unknown>): ChatSearch {
  return { tab: isChatTab(search.tab) ? search.tab : undefined };
}

/** The tab to show: the requested one, else the room. */
export function resolveChatTab(requested: ChatTab | undefined): ChatTab {
  return requested ?? "room";
}

/** The durations a staff mute offers; `null` = until someone unmutes. */
export const CHAT_MUTE_DURATIONS = {
  "3600": "1 hour",
  "86400": "1 day",
  "604800": "7 days",
  indefinite: "Until unmuted",
} as const;
export type ChatMuteDuration = keyof typeof CHAT_MUTE_DURATIONS;

export function isChatMuteDuration(value: unknown): value is ChatMuteDuration {
  return typeof value === "string" && value in CHAT_MUTE_DURATIONS;
}

/** The mutation's `durationSeconds`: undefined for an indefinite mute. */
export function muteDurationSeconds(duration: ChatMuteDuration): number | undefined {
  return duration === "indefinite" ? undefined : Number(duration);
}

export const CHAT_REPORT_STATUS_LABELS = {
  OPEN: "Open",
  RESOLVED: "Resolved",
  DISMISSED: "Dismissed",
} as const;
export type ChatReportStatus = keyof typeof CHAT_REPORT_STATUS_LABELS;

export function isChatReportStatus(value: unknown): value is ChatReportStatus {
  return typeof value === "string" && value in CHAT_REPORT_STATUS_LABELS;
}

export const CHAT_REPORT_CATEGORY_LABELS: Record<string, string> = {
  HARASSING: "Harassment",
  INAPPROPRIATE: "Inappropriate",
  SPAM: "Spam",
  SUSPICIOUS: "Suspicious",
};

/** "Deleted by staff" / "Deleted by sender": `deletedBy` is `self` or `staff:{userId}`. */
export function deletedByLabel(deletedBy: string | undefined): string {
  return deletedBy === "self" ? "Deleted by sender" : "Deleted by staff";
}

type Outputs = TRPCOptionsProxy<AppRouter>["chat"];
export type ChatStaffMessage = inferOutput<Outputs["messages"]>["items"][number];
export type ChatReportRow = inferOutput<Outputs["reports"]>["items"][number];

/** Refetches every chat read (room, reports, mutes, a user's messages) after a moderation write. */
export function useInvalidateChat() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: trpc.chat.pathKey() });
}
